import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Lightweight markdown renderer tuned for the assistant chat bubbles.
 * Keeps the graphite-slate theme (small text, muted borders, inline mono).
 */
export default function MarkdownMessage({ children }) {
  return (
    <div className="assistant-md text-sm leading-relaxed break-words">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => (
            <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...props} />
          ),
          strong: ({ node, ...props }) => (
            <strong className="font-semibold text-ink-primary" {...props} />
          ),
          em: ({ node, ...props }) => <em className="italic" {...props} />,
          a: ({ node, ...props }) => (
            <a
              {...props}
              target="_blank"
              rel="noreferrer noopener"
              className="underline underline-offset-2 text-ink-primary hover:opacity-80"
            />
          ),
          ul: ({ node, ...props }) => (
            <ul className="list-disc pl-5 mb-2 last:mb-0 space-y-1" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="list-decimal pl-5 mb-2 last:mb-0 space-y-1" {...props} />
          ),
          li: ({ node, ...props }) => <li className="marker:text-ink-secondary" {...props} />,
          h1: ({ node, ...props }) => (
            <h3 className="font-display text-lg tracking-tight mt-2 mb-2" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h3 className="font-display text-base tracking-tight mt-2 mb-2" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h4 className="font-display text-sm tracking-tight mt-2 mb-1.5" {...props} />
          ),
          h4: ({ node, ...props }) => (
            <h5 className="font-medium text-sm mt-2 mb-1" {...props} />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote
              className="border-l-2 border-white/15 pl-3 my-2 text-ink-secondary italic"
              {...props}
            />
          ),
          code: ({ node, inline, className, children, ...props }) => {
            if (inline) {
              return (
                <code
                  className="rounded bg-graphite-950 border border-white/10 px-1 py-0.5 text-[12px] font-mono"
                  {...props}
                >
                  {children}
                </code>
              );
            }
            return (
              <pre className="my-2 overflow-x-auto rounded-md bg-graphite-950 border border-white/10 p-3 text-[12px] font-mono">
                <code {...props}>{children}</code>
              </pre>
            );
          },
          hr: () => <hr className="my-3 border-white/10" />,
          table: ({ node, ...props }) => (
            <div className="my-2 overflow-x-auto">
              <table className="w-full text-left text-xs border border-white/10" {...props} />
            </div>
          ),
          th: ({ node, ...props }) => (
            <th className="border border-white/10 px-2 py-1 bg-graphite-950 font-medium" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="border border-white/10 px-2 py-1 align-top" {...props} />
          ),
        }}
      >
        {children || ""}
      </ReactMarkdown>
    </div>
  );
}
